/** Web Apple Pay capability adapter. */
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import { ApplePayFailureReason } from './types';

interface IApplePaySessionCapability {
  canMakePayments(): boolean;
}

export interface IApplePayWebCapabilityInputs {
  applePaySessionAvailable: boolean;
  canMakePayments: boolean;
  canMakePaymentsError: boolean;
  applePayPluginAvailable: boolean;
  isApplePayWebEnabled: boolean;
}

export interface IApplePayWebCapability extends IApplePayWebCapabilityInputs {
  canUseApplePay: boolean;
  reasons: ApplePayFailureReason[];
}

interface IGetApplePayWebCapability {
  applePaySession?: IApplePaySessionCapability;
  applePayPluginAvailable: boolean;
  isApplePayWebEnabled: boolean;
}

export const evaluateApplePayWebCapability = ({
  applePaySessionAvailable,
  canMakePayments,
  canMakePaymentsError,
  applePayPluginAvailable,
  isApplePayWebEnabled,
}: IApplePayWebCapabilityInputs): IApplePayWebCapability => {
  const reasons: ApplePayFailureReason[] = [];

  if (!applePaySessionAvailable) {
    reasons.push(ApplePayFailureReason.APPLE_PAY_SESSION_UNAVAILABLE);
  } else if (canMakePaymentsError) {
    reasons.push(ApplePayFailureReason.CAN_MAKE_PAYMENTS_ERROR);
  } else if (!canMakePayments) {
    reasons.push(ApplePayFailureReason.CAN_MAKE_PAYMENTS_FALSE);
  }

  if (!applePayPluginAvailable) {
    reasons.push(ApplePayFailureReason.ENABLE_APPLE_PAY_DISABLED);
  }

  if (!isApplePayWebEnabled) {
    reasons.push(ApplePayFailureReason.ENABLE_APPLE_PAY_WEB_DISABLED);
  }

  return {
    applePaySessionAvailable,
    canMakePayments,
    canMakePaymentsError,
    applePayPluginAvailable,
    isApplePayWebEnabled,
    canUseApplePay: reasons.length === 0,
    reasons,
  };
};

export const getApplePayWebCapability = ({
  applePaySession,
  applePayPluginAvailable,
  isApplePayWebEnabled,
}: IGetApplePayWebCapability): IApplePayWebCapability => {
  let canMakePayments = false;
  let canMakePaymentsError = false;

  if (applePaySession) {
    try {
      canMakePayments = applePaySession.canMakePayments();
    } catch {
      canMakePaymentsError = true;
    }
  }

  return evaluateApplePayWebCapability({
    applePaySessionAvailable: Boolean(applePaySession),
    canMakePayments,
    canMakePaymentsError,
    applePayPluginAvailable,
    isApplePayWebEnabled,
  });
};

export function useApplePayWebCapability(): IApplePayWebCapability {
  const applePayPluginAvailable = useFlag(LaunchDarklyFlag.ENABLE_APPLE_PAY);
  const isApplePayWebEnabled = useFlag(LaunchDarklyFlag.ENABLE_APPLE_PAY_WEB);

  return getApplePayWebCapability({
    applePaySession: window.ApplePaySession,
    applePayPluginAvailable,
    isApplePayWebEnabled,
  });
}

export default function useCanUseApplePay() {
  return useApplePayWebCapability().canUseApplePay;
}
